import { useEffect, useRef, useState, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, Users, Crown, Send, LogOut, Trophy, Loader2 } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { buildAvatarUrl, defaultAvatar } from "@/lib/avatar";

export default function CrewDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [crew, setCrew] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [joining, setJoining] = useState(false);
  const chatBoxRef = useRef(null);

  const isMember = user && crew && crew.members?.some((m) => m.user_id === user.user_id);

  const loadCrew = useCallback(async () => {
    try {
      const { data } = await api.get(`/crews/${id}`);
      setCrew(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Crew not found");
      nav("/crews");
    }
  }, [id, nav]);

  const loadMessages = useCallback(async () => {
    try {
      const { data } = await api.get(`/crews/${id}/messages?limit=80`);
      setMessages(data);
    } catch {
      /* ignore */
    }
  }, [id]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await Promise.all([loadCrew(), loadMessages()]);
      setLoading(false);
    })();
  }, [loadCrew, loadMessages]);

  // Poll messages every 4s if member
  useEffect(() => {
    if (!isMember) return undefined;
    const t = setInterval(loadMessages, 4000);
    return () => clearInterval(t);
  }, [isMember, loadMessages]);

  // Auto-scroll chat
  useEffect(() => {
    if (chatBoxRef.current) {
      chatBoxRef.current.scrollTop = chatBoxRef.current.scrollHeight;
    }
  }, [messages]);

  const send = async (e) => {
    e.preventDefault();
    const t = text.trim();
    if (!t) return;
    setSending(true);
    try {
      await api.post(`/crews/${id}/messages`, { text: t });
      setText("");
      await loadMessages();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send");
    } finally {
      setSending(false);
    }
  };

  const join = async () => {
    if (!user) { nav("/login"); return; }
    setJoining(true);
    try {
      await api.post(`/crews/${id}/join`);
      toast.success("Joined the crew!");
      await loadCrew();
      await loadMessages();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not join");
    } finally {
      setJoining(false);
    }
  };

  const leave = async () => {
    if (!window.confirm("Are you sure you want to leave the crew?")) return;
    setJoining(true);
    try {
      const { data } = await api.post(`/crews/${id}/leave`);
      toast.success(data.deleted ? "Crew deleted (you were the last member)" : "Left the crew");
      nav("/crews");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not leave");
    } finally {
      setJoining(false);
    }
  };

  if (loading || !crew) {
    return (
      <div className="min-h-[60vh] grid place-items-center">
        <Loader2 className="w-8 h-8 animate-spin text-brand-magenta" />
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-4rem)]" data-testid="crew-detail-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <Link to="/crews" className="btn-ghost !pl-2 mb-6" data-testid="crew-back-btn">
          <ArrowLeft className="w-4 h-4" /> All crews
        </Link>

        {/* Header */}
        <div className="card-glass p-6 sm:p-8 mb-6 relative overflow-hidden">
          <div
            className="absolute inset-0 opacity-30"
            style={{ background: `radial-gradient(ellipse at top left, #${crew.color}, transparent 60%)` }}
          />
          <div className="relative flex flex-col sm:flex-row items-start sm:items-center gap-5">
            <div
              className="w-20 h-20 rounded-3xl grid place-items-center font-display font-extrabold text-white text-2xl shadow-lift shrink-0"
              style={{ background: `linear-gradient(135deg, #${crew.color}, #3E7A5C)` }}
            >
              {crew.tag.slice(0, 2)}
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="font-display font-extrabold text-3xl sm:text-4xl text-white tracking-tighter">{crew.name}</h1>
              <div className="font-mono text-sm text-white/60 mt-1">[{crew.tag}]</div>
              {crew.motto && <p className="text-white/70 mt-2 italic">&ldquo;{crew.motto}&rdquo;</p>}
            </div>
            <div className="flex flex-col sm:items-end gap-2 shrink-0">
              <div className="flex items-center gap-4">
                <Stat label="Members" value={crew.member_count} icon={Users} />
                <Stat label="USDT" value={crew.total_won.toFixed(0)} icon={Trophy} />
              </div>
              <div className="flex gap-2 mt-1">
                {isMember ? (
                  <button onClick={leave} disabled={joining} className="btn-ghost text-xs !py-1.5" data-testid="leave-crew-btn">
                    <LogOut className="w-3.5 h-3.5" /> Leave
                  </button>
                ) : (
                  <button onClick={join} disabled={joining} className="btn-primary !py-2 !px-5" data-testid="join-crew-btn">
                    {joining ? "Joining..." : "Join Crew"}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Layout: members | chat */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Members / leaderboard */}
          <div className="lg:col-span-4">
            <div className="card-glass p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="label-mono">crew leaderboard</div>
                <Trophy className="w-4 h-4 text-brand-amber" />
              </div>
              <div className="space-y-2 max-h-[480px] overflow-y-auto pr-1">
                {crew.members.map((m, i) => (
                  <div key={m.user_id} className="flex items-center gap-3 p-2 rounded-xl hover:bg-white/5" data-testid={`member-${m.user_id}`}>
                    <span className="w-6 text-center font-mono font-bold text-white/40 text-sm">{i + 1}</span>
                    <img src={buildAvatarUrl(m.avatar || defaultAvatar(m.user_id), 80)} alt={m.name} className="w-9 h-9 rounded-full border border-white/15 bg-ink-700" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-white truncate flex items-center gap-1.5">
                        {m.name}
                        {m.user_id === crew.owner_id && <Crown className="w-3 h-3 text-brand-amber" />}
                      </div>
                      <div className="text-xs font-mono text-white/50">{m.total_won.toFixed(0)} USDT</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Chat */}
          <div className="lg:col-span-8">
            <div className="card-glass flex flex-col h-[600px]">
              <div className="flex items-center justify-between p-4 border-b border-white/10">
                <div className="flex items-center gap-2">
                  <span className="dot-live" />
                  <div className="font-display font-bold">Crew Chat</div>
                </div>
                <div className="label-mono">{messages.length} messages</div>
              </div>
              <div ref={chatBoxRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="crew-chat-messages">
                {messages.length === 0 && (
                  <div className="text-center text-white/40 py-10">No messages yet — be the first.</div>
                )}
                <AnimatePresence initial={false}>
                  {messages.map((m) => (
                    <motion.div
                      key={m.msg_id}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`flex items-start gap-2.5 ${m.system ? "justify-center" : ""}`}
                    >
                      {m.system ? (
                        <div className="text-xs font-mono text-white/40 italic">— {m.text} —</div>
                      ) : (
                        <>
                          <img
                            src={buildAvatarUrl(m.avatar || defaultAvatar(m.user_id), 60)}
                            alt={m.name}
                            className="w-8 h-8 rounded-full border border-white/10 bg-ink-700 shrink-0"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="text-xs font-mono text-white/60">{m.name}</div>
                            <div className="text-sm text-white bg-white/5 rounded-2xl rounded-tl-md px-3 py-1.5 inline-block max-w-full break-words">{m.text}</div>
                          </div>
                        </>
                      )}
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
              <form onSubmit={send} className="p-3 border-t border-white/10 flex gap-2">
                {isMember ? (
                  <>
                    <input
                      type="text"
                      value={text}
                      onChange={(e) => setText(e.target.value)}
                      className="field !py-2 flex-1"
                      placeholder="Type a message…"
                      disabled={sending}
                      maxLength={240}
                      data-testid="crew-chat-input"
                    />
                    <button
                      type="submit"
                      disabled={sending || !text.trim()}
                      className="btn-primary !py-2 !px-4"
                      data-testid="crew-chat-send-btn"
                    >
                      <Send className="w-4 h-4" />
                    </button>
                  </>
                ) : (
                  <div className="flex-1 text-center text-sm text-white/50 py-2 font-mono" data-testid="crew-chat-locked">
                    Members only — tap the button above to join
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, icon: Icon }) {
  return (
    <div className="text-right">
      <div className="label-mono">{label}</div>
      <div className="flex items-center gap-1.5 mt-0.5">
        <Icon className="w-3.5 h-3.5 text-brand-cyan" />
        <div className="font-mono font-bold text-white text-lg">{value}</div>
      </div>
    </div>
  );
}
